import { useEffect, useState } from "react";
import { User, UserPlus, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { formatApiError } from "../../lib/auth";
import StepShell from "./StepShell";

/**
 * Step 1 — Start.
 *
 * Two paths:
 *   • "Continue as guest" — only a first name required to begin.
 *   • "Create an account" — first/last/phone/email/password.
 *
 * If the user is already authenticated we skip the form and pull their
 * name directly. They can still continue.
 */
export default function StartStep({ byod, onContinue }) {
  const { user, signup } = useAuth();
  const [mode, setMode] = useState(byod.state.identity?.mode || null);
  const [firstName, setFirstName] = useState(byod.state.identity?.first_name || "");
  const [lastName, setLastName] = useState(byod.state.identity?.last_name || "");
  const [phone, setPhone] = useState(byod.state.identity?.phone || "");
  const [email, setEmail] = useState(byod.state.identity?.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // If already logged in, lock in account mode and prefill.
  useEffect(() => {
    if (user && typeof user === "object") {
      const parts = String(user.name || "").trim().split(/\s+/);
      const fn = parts[0] || "";
      const ln = parts.slice(1).join(" ") || "";
      setMode("account_existing");
      setFirstName((v) => v || fn);
      setLastName((v) => v || ln);
      setPhone((v) => v || user.phone || "");
      setEmail((v) => v || user.email || "");
      byod.update("identity", {
        mode: "account_existing",
        first_name: fn,
        last_name: ln,
        phone: user.phone || "",
        email: user.email || "",
      });
      // sync contact too so review/submit step is prefilled
      byod.update("contact", {
        first_name: fn || byod.state.contact?.first_name,
        last_name: ln || byod.state.contact?.last_name,
        phone: user.phone || byod.state.contact?.phone,
        email: user.email || byod.state.contact?.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submitGuest = (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!firstName.trim()) {
      setErr("Please enter your first name so Scott knows who he's talking to.");
      return;
    }
    byod.update("identity", { mode: "guest", first_name: firstName.trim() });
    byod.update("contact", { first_name: firstName.trim() });
    onContinue();
  };

  const submitAccount = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!firstName.trim() || !lastName.trim()) return setErr("First name and last name are required.");
    if (!email.trim()) return setErr("Email is required to create an account.");
    if (!phone.trim()) return setErr("Phone number is required to create an account.");
    if (!password || password.length < 8) return setErr("Password must be at least 8 characters.");

    setBusy(true);
    try {
      await signup({
        name: `${firstName.trim()} ${lastName.trim()}`,
        email: email.trim(),
        phone: phone.trim(),
        password,
      });
      byod.update("identity", {
        mode: "account",
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      byod.update("contact", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      onContinue();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (mode === "account_existing" && user) {
    return (
      <StepShell
        title={`Welcome back, ${firstName || "there"}!`}
        subtitle="Let's pick up where you left off — Scott will personally review your deal when you're ready."
        onContinue={() => {
          byod.update("identity", {
            mode: "account_existing",
            first_name: firstName,
            last_name: lastName,
            phone,
            email,
          });
          onContinue();
        }}
        showBack={false}
        testIdPrefix="byod-start"
      >
        <div className="rounded-2xl border-2 border-[var(--ssc-blue)] bg-blue-50/60 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--ssc-blue)] font-bold">Signed in as</div>
          <div className="font-extrabold text-[var(--ssc-navy)] text-lg mt-0.5">{firstName} {lastName}</div>
          <div className="text-sm text-[var(--ssc-ink-2)] mt-0.5">{email}{phone ? ` · ${phone}` : ""}</div>
          <p className="text-sm text-[var(--ssc-ink)] mt-3">
            Because you&rsquo;re signed in, your deal will save automatically — you can pick it
            back up any time from this device or another.
          </p>
        </div>
      </StepShell>
    );
  }

  if (mode === null) {
    return (
      <StepShell
        title="Hey there — let's build a deal together."
        subtitle="Scott will walk you through it one step at a time. First, how would you like to start?"
        showBack={false}
        showContinue={false}
        testIdPrefix="byod-start"
      >
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => setMode("guest")}
            className="text-left rounded-2xl border-2 border-[var(--ssc-line)] bg-white p-5 hover:border-[var(--ssc-blue)] transition-colors"
            data-testid="byod-start-as-guest"
          >
            <div className="w-12 h-12 rounded-xl bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] grid place-items-center">
              <User size={22} />
            </div>
            <div className="mt-3 font-extrabold text-[var(--ssc-navy)] text-lg">Continue as guest</div>
            <p className="text-sm text-[var(--ssc-ink-2)] mt-1 leading-snug">
              Just your first name — no phone, no email required to start building.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setMode("account")}
            className="text-left rounded-2xl border-2 border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.04] p-5 hover:bg-[var(--ssc-blue)]/[0.08] transition-colors"
            data-testid="byod-start-create-account"
          >
            <div className="w-12 h-12 rounded-xl bg-[var(--ssc-blue)] text-white grid place-items-center">
              <UserPlus size={22} />
            </div>
            <div className="mt-3 font-extrabold text-[var(--ssc-navy)] text-lg">Create an account</div>
            <p className="text-sm text-[var(--ssc-ink-2)] mt-1 leading-snug">
              Save your progress and come back later. Recommended if you want
              to compare options across sessions.
            </p>
          </button>
        </div>
      </StepShell>
    );
  }

  if (mode === "guest") {
    return (
      <StepShell
        title="What should Scott call you?"
        subtitle="Just your first name to start — no phone or email needed yet."
        onContinue={submitGuest}
        onBack={() => setMode(null)}
        backLabel="Different option"
        testIdPrefix="byod-start"
      >
        <form onSubmit={submitGuest} className="space-y-3 max-w-md">
          <label className="block">
            <span className="ssc-label">First name *</span>
            <input
              autoFocus
              className="ssc-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              data-testid="byod-start-guest-firstname"
            />
          </label>
          {err && <div className="text-sm text-[var(--ssc-red)]" data-testid="byod-start-error">{err}</div>}
        </form>
      </StepShell>
    );
  }

  // Account creation
  return (
    <StepShell
      title="Create your account"
      subtitle="Required to save your deal and come back later. Scott uses this to follow up only — never to spam you."
      onContinue={submitAccount}
      onBack={() => setMode(null)}
      backLabel="Different option"
      continueLabel={busy ? "Creating..." : "Create & Continue"}
      continueDisabled={busy}
      testIdPrefix="byod-start"
    >
      <form onSubmit={submitAccount} className="grid sm:grid-cols-2 gap-3 max-w-2xl">
        <label className="block">
          <span className="ssc-label">First name *</span>
          <input className="ssc-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" data-testid="byod-start-account-firstname" />
        </label>
        <label className="block">
          <span className="ssc-label">Last name *</span>
          <input className="ssc-input" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" data-testid="byod-start-account-lastname" />
        </label>
        <label className="block">
          <span className="ssc-label">Phone *</span>
          <input className="ssc-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" data-testid="byod-start-account-phone" />
        </label>
        <label className="block">
          <span className="ssc-label">Email *</span>
          <input className="ssc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="byod-start-account-email" />
        </label>
        <label className="block sm:col-span-2">
          <span className="ssc-label">Password * <span className="text-xs font-normal text-[var(--ssc-ink-2)]">(8+ characters)</span></span>
          <input className="ssc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="byod-start-account-password" />
        </label>
        {err && <div className="sm:col-span-2 text-sm text-[var(--ssc-red)]" data-testid="byod-start-error">{err}</div>}
        {busy && (
          <div className="sm:col-span-2 text-sm text-[var(--ssc-ink-2)] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Creating your account...
          </div>
        )}
      </form>
    </StepShell>
  );
}
